import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../api";
import type { GuardMode, GuardStatus } from "@glosor/shared";

function DeviceToken({ token }: { token: string | null }) {
  const [revealed, setRevealed] = useState(false);
  const [copied, setCopied] = useState(false);

  if (token === null) {
    return (
      <div className="text-xs text-slate-500 dark:text-slate-400">
        Device token: not configured — set <code>MCP_TOKEN</code> in the server&#39;s{" "}
        <code>.env</code> to use the Guard app.
      </div>
    );
  }

  async function copy() {
    try {
      await navigator.clipboard.writeText(token!);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {}
  }

  return (
    <div className="space-y-1">
      <div className="text-sm font-medium">Device token (MCP_TOKEN)</div>
      <div className="text-xs text-slate-500 dark:text-slate-400">
        Paste this into the Glosor Guard app (or any MCP client) as the bearer token.
      </div>
      <div className="flex items-center gap-2">
        <code className="input font-mono text-xs flex-1 min-w-0 truncate py-2">
          {revealed ? token : "•".repeat(Math.min(token.length, 32))}
        </code>
        <button type="button" className="btn-secondary text-sm" onClick={() => setRevealed(!revealed)}>
          {revealed ? "Hide" : "Show"}
        </button>
        <button type="button" className="btn-secondary text-sm" onClick={copy}>
          {copied ? "Copied!" : "Copy"}
        </button>
      </div>
    </div>
  );
}

const MODE_OPTIONS: { value: GuardMode; label: string; description: string }[] = [
  {
    value: "daily_clean",
    label: "Until decks are clean today",
    description:
      "Blocked until every deck has been fully reviewed once today. After that you're free for the rest of the day, even if learning cards come back.",
  },
  {
    value: "always",
    label: "Whenever cards are waiting",
    description: "Blocked any time there is something to review right now.",
  },
];

function statusLine(status: GuardStatus): { text: string; tone: "blocked" | "free" | "off" } {
  if (!status.enabled) return { text: "Guard is disabled.", tone: "off" };
  const { due, newRemaining } = status.workload;
  if (status.blocked) {
    return {
      text: `Blocked — ${due} due + ${newRemaining} new remaining.`,
      tone: "blocked",
    };
  }
  return {
    text:
      status.mode === "daily_clean"
        ? "Free — decks were cleaned today."
        : "Free — nothing to review right now.",
    tone: "free",
  };
}

export default function GuardSettings() {
  const qc = useQueryClient();
  const settings = useQuery({ queryKey: ["guardSettings"], queryFn: () => api.getGuardSettings() });

  const [enabled, setEnabled] = useState(false);
  const [mode, setMode] = useState<GuardMode>("daily_clean");
  const [domainsText, setDomainsText] = useState("");
  const [packagesText, setPackagesText] = useState("");

  useEffect(() => {
    if (!settings.data) return;
    setEnabled(settings.data.enabled);
    setMode(settings.data.mode);
    setDomainsText(settings.data.blockedDomains.join("\n"));
    setPackagesText(settings.data.blockedPackages.join("\n"));
  }, [settings.data]);

  const save = useMutation({
    mutationFn: () =>
      api.updateGuardSettings({
        enabled,
        mode,
        blockedDomains: domainsText.split("\n").map((s) => s.trim()).filter(Boolean),
        blockedPackages: packagesText.split("\n").map((s) => s.trim()).filter(Boolean),
      }),
    onSuccess: (data) => qc.setQueryData(["guardSettings"], data),
  });

  if (settings.isLoading) {
    return (
      <section className="card-surface p-4 text-slate-500 dark:text-slate-400">
        Loading guard settings…
      </section>
    );
  }
  if (!settings.data) return null;

  const line = statusLine(settings.data.status);

  return (
    <section className="card-surface p-4 space-y-4">
      <div className="text-xs uppercase tracking-wide text-slate-500">Distraction guard</div>

      <div
        className={
          line.tone === "blocked"
            ? "text-sm text-red-600 dark:text-red-400"
            : line.tone === "free"
              ? "text-sm text-green-600 dark:text-green-400"
              : "text-sm text-slate-500 dark:text-slate-400"
        }
      >
        {line.text}
      </div>

      <p className="text-sm text-slate-500 dark:text-slate-400">
        The Glosor Guard Android app redirects the sites and apps below into Glosor while you still
        have cards to review.
      </p>

      <label className="flex items-center gap-2 text-sm">
        <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
        Enable guard
      </label>

      <div className="space-y-2">
        {MODE_OPTIONS.map((opt) => (
          <label key={opt.value} className="flex items-start gap-2 text-sm">
            <input
              type="radio"
              name="guard-mode"
              className="mt-1"
              checked={mode === opt.value}
              onChange={() => setMode(opt.value)}
            />
            <span>
              <span className="font-medium">{opt.label}</span>
              <span className="block text-xs text-slate-500 dark:text-slate-400">
                {opt.description}
              </span>
            </span>
          </label>
        ))}
      </div>

      <label className="block text-sm space-y-1">
        <span className="font-medium">Blocked websites (one per line)</span>
        <textarea
          className="input font-mono text-xs min-h-24"
          placeholder={"dn.se\nnews.ycombinator.com\nfacebook.com"}
          value={domainsText}
          onChange={(e) => setDomainsText(e.target.value)}
        />
      </label>

      <label className="block text-sm space-y-1">
        <span className="font-medium">Blocked Android apps (package names, one per line)</span>
        <textarea
          className="input font-mono text-xs min-h-24"
          placeholder={"com.devhd.feedly\ncom.facebook.katana"}
          value={packagesText}
          onChange={(e) => setPackagesText(e.target.value)}
        />
      </label>

      <DeviceToken token={settings.data.mcpToken} />

      <div className="flex items-center gap-3">
        <button
          type="button"
          className="btn-primary"
          disabled={save.isPending}
          onClick={() => save.mutate()}
        >
          Save
        </button>
        {save.isSuccess && !save.isPending && (
          <span className="text-xs text-slate-500 dark:text-slate-400">Saved.</span>
        )}
        {save.isError && (
          <span className="text-xs text-red-600 dark:text-red-400">Save failed.</span>
        )}
      </div>
    </section>
  );
}
